import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const idempotencyKey = z
  .string()
  .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/)
const reasonCode = z.string().regex(/^[a-z][a-z0-9_]{1,63}$/)

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey,
  movementId: entityIdSchema,
  expectedVersion: z.literal(0),
  visitId: entityIdSchema,
  expectedVisitVersion: z.number().int().positive(),
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
  locationId: entityIdSchema,
  itemMasterId: entityIdSchema,
  expectedStockVersion: z.number().int().nonnegative(),
  quantityMilli: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  postingDate: z.string().date(),
  reasonCode,
}

export const visitMaterialIssueV1Schema = z
  .object({
    ...base,
    billingDisposition: z.enum(["billable", "included", "non_billable"]),
    billingReferenceId: entityIdSchema.nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (
      (value.billingDisposition === "billable") !==
      (value.billingReferenceId !== null)
    ) {
      context.addIssue({
        code: "custom",
        message: "Billable material requires one billing reference.",
      })
    }
  })

export const visitMaterialReturnV1Schema = z
  .object({
    ...base,
    sourceMovementId: entityIdSchema,
  })
  .strict()

export const visitMaterialMovementResultV1Schema = z
  .object({
    entityType: z.literal("visit_material_movement"),
    entityId: entityIdSchema,
    state: z.literal("posted"),
    version: z.literal(1),
    movementKind: z.enum(["issue", "return"]),
    visitId: entityIdSchema,
    workerId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    locationId: entityIdSchema,
    itemMasterId: entityIdSchema,
    quantityMilli: z.number().int().positive(),
    sourceMovementId: entityIdSchema.nullable(),
    stockEntry: z.string().min(1).max(140),
    valuationAmountMinor: z.number().int().nonnegative(),
    visitCostImpactMinor: z.number().int(),
    currency: z.string().regex(/^[A-Z]{3}$/),
    billingDisposition: z.enum(["billable", "included", "non_billable"]),
    billingReferenceId: entityIdSchema.nullable(),
    stockPosted: z.literal(true),
  })
  .strict()

export type VisitMaterialIssueV1 = z.infer<typeof visitMaterialIssueV1Schema>
export type VisitMaterialReturnV1 = z.infer<typeof visitMaterialReturnV1Schema>
export type VisitMaterialMovementResultV1 = z.infer<
  typeof visitMaterialMovementResultV1Schema
>
